import { useEffect, useMemo } from "react";
import {
  AdvancedMarker,
  Map as GoogleMap,
  useMap,
} from "@vis.gl/react-google-maps";

import type { CarrierTripDetail } from "@/api/carrier";
import { LANE_META, LANE_ORDER } from "./task-type-meta";
import {
  decodePolyline,
  encodedRoutePolyline,
  stopLatLng,
  type LatLngLiteral,
} from "./route-geometry";
import type { DerivedStop } from "./trip-stops";

/**
 * The trip's stops on a map, beside the list.
 *
 * Ported from admin-web's focused map. Sequencing from a list alone cannot
 * answer "is stop 4 on the way to stop 5?", which is the question a dispatcher
 * is actually asking when they open a trip.
 *
 * The route line is drawn ONLY from stored geometry. A trip with no computed
 * route gets no line at all — joining its stops with straight segments would
 * draw a road that does not exist.
 */

// Read at render, not at import. A module-level const is evaluated before a
// test can stub the env, so this module would decide "no maps key" once, at
// import time, and never reconsider — green locally where a .env exists, red
// anywhere it does not. `map-shell` reads it the same way for the same reason.
const mapsConfigured = () => Boolean(import.meta.env.VITE_GOOGLE_MAPS_API_KEY);
const MAP_ID = "3667d2e0a1e93d2b";

function StopPin({
  stop,
  color,
  selected,
}: {
  stop: DerivedStop;
  color: string;
  selected: boolean;
}) {
  const lanes = LANE_ORDER.filter((lane) => stop.counts[lane] > 0);
  const background = stop.status === "done" ? "#94a3b8" : color;

  return (
    <span
      title={
        stop.status === "done"
          ? "Resolved stop: all tasks completed, failed, or cancelled"
          : undefined
      }
      className={`flex flex-col items-center ${selected ? "scale-110" : "scale-100"}`}
      style={{ transition: "transform 120ms" }}
    >
      <span
        className="grid h-6 w-6 place-items-center rounded-md font-mono text-[11px] font-bold tabular-nums text-white"
        style={{
          background,
          boxShadow: selected
            ? "0 0 0 3px rgba(79,70,229,.45)"
            : "0 1px 3px rgba(15,15,15,.35)",
        }}
      >
        {stop.seq}
      </span>
      {lanes.length > 0 && (
        <span className="mt-[2px] flex gap-[2px]">
          {lanes.map((lane) => (
            <span
              key={lane}
              className="h-[5px] w-[5px] rounded-[1px]"
              style={{ background: LANE_META[lane].color }}
            />
          ))}
        </span>
      )}
    </span>
  );
}

/** Draws the stored route and fits the map to everything it can see. */
function RouteOverlay({
  detail,
  points,
  color,
}: {
  detail: CarrierTripDetail;
  points: LatLngLiteral[];
  color: string;
}) {
  const map = useMap();
  const encoded =
    encodedRoutePolyline(detail.actualRoute) ??
    encodedRoutePolyline(detail.plannedRoute);

  useEffect(() => {
    if (!map) return;

    const bounds = new google.maps.LatLngBounds();
    let anything = false;
    for (const point of points) {
      bounds.extend(point);
      anything = true;
    }

    let line: google.maps.Polyline | null = null;
    if (encoded) {
      const path = decodePolyline(encoded);
      if (path.length > 1) {
        line = new google.maps.Polyline({
          path,
          strokeColor: color,
          strokeOpacity: 0.9,
          strokeWeight: 4,
        });
        line.setMap(map);
        for (const point of path) {
          bounds.extend(point);
          anything = true;
        }
      }
    }

    if (anything) map.fitBounds(bounds, 56);
    return () => line?.setMap(null);
  }, [map, points, encoded, color]);

  return null;
}

export function FocusTripMap({
  detail,
  stops,
  selectedId,
  onSelect,
}: {
  detail: CarrierTripDetail;
  stops: DerivedStop[];
  selectedId: string | null;
  onSelect: (stop: DerivedStop) => void;
}) {
  const color = detail.color ?? "#4f46e5";
  const located = useMemo(
    () =>
      stops
        .map((stop) => {
          const waypoint = detail.waypoints.find(
            (point) => point.id === stop.id
          );
          return { stop, at: waypoint ? stopLatLng(waypoint) : null };
        })
        .filter((entry): entry is { stop: DerivedStop; at: LatLngLiteral } =>
          Boolean(entry.at)
        ),
    [stops, detail.waypoints]
  );
  const points = useMemo(() => located.map((entry) => entry.at), [located]);
  const hasRoute = Boolean(
    encodedRoutePolyline(detail.actualRoute) ??
      encodedRoutePolyline(detail.plannedRoute)
  );

  if (!mapsConfigured()) {
    return (
      <Pane>
        <p className="text-[12px] text-zinc-500">
          Map unavailable — no maps key is configured for this build.
        </p>
      </Pane>
    );
  }

  if (located.length === 0) {
    return (
      <Pane>
        <p className="text-[12px] text-zinc-500">
          No stop on this trip has a location yet.
        </p>
      </Pane>
    );
  }

  return (
    <div className="relative h-full min-h-64 w-full">
      <GoogleMap
        defaultCenter={points[0]}
        defaultZoom={12}
        gestureHandling="greedy"
        disableDefaultUI
        mapId={MAP_ID}
        style={{ width: "100%", height: "100%" }}
      >
        <RouteOverlay detail={detail} points={points} color={color} />
        {located.map(({ stop, at }) => (
          <AdvancedMarker
            key={stop.id}
            position={at}
            title={`Stop ${stop.seq}: ${stop.addr}`}
            onClick={() => onSelect(stop)}
          >
            <StopPin
              stop={stop}
              color={color}
              selected={selectedId === stop.id}
            />
          </AdvancedMarker>
        ))}
      </GoogleMap>

      <div
        className="pointer-events-none absolute left-3 top-3 flex items-center gap-1.5 rounded-lg bg-white/95 px-2.5 py-1.5 text-[11px] font-semibold text-zinc-700"
        style={{ boxShadow: "0 1px 3px rgba(15,15,15,.12)" }}
      >
        <span
          className="rounded-[3px]"
          style={{ width: 10, height: 10, background: color }}
        />
        TRP-{detail.tripNumber} · {located.length}{" "}
        {located.length === 1 ? "stop" : "stops"}
      </div>

      {!hasRoute && (
        <div
          className="pointer-events-none absolute bottom-3 left-3 rounded-lg bg-white/95 px-2.5 py-1.5 text-[11px] font-medium text-zinc-600"
          style={{ boxShadow: "0 1px 3px rgba(15,15,15,.12)" }}
        >
          No route computed yet
        </div>
      )}
    </div>
  );
}

function Pane({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid h-full min-h-48 place-items-center bg-zinc-50 px-6 text-center">
      {children}
    </div>
  );
}
